import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '~/player/api/client'
import { connectCable, disconnectCable } from '~/player/services/cable'
import { useGameNavStore } from '~/player/stores/gameNav'

const REFRESH_COOLDOWN_MS = 3_000  // 手動 reload 按鈕冷卻時間

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('player_token'))
  const walletBalance = ref(null)
  const walletMode = ref(null)
  const walletRefreshing = ref(false)
  const playerAccount = ref(null)
  const locale = ref(localStorage.getItem('player_locale') || 'en')
  const isAuthenticated = computed(() => !!token.value)
  const isSeamless = computed(() => walletMode.value === 'seamless')

  let lastRefreshAt = 0

  // 快取 fetchProfile promise，多次呼叫只發一次 request
  let profilePromise = null

  // WebSocket 訂閱（ActionCable）
  let walletSubscription = null
  const wsConnected = ref(false)

  function setToken(newToken) {
    token.value = newToken
    localStorage.setItem('player_token', newToken)
  }

  function setLocale(newLocale) {
    locale.value = newLocale
    localStorage.setItem('player_locale', newLocale)
  }

  function clear() {
    token.value = null
    walletBalance.value = null
    walletMode.value = null
    playerAccount.value = null
    profilePromise = null
    localStorage.removeItem('player_token')
    unsubscribeWallet()
    useGameNavStore().reset()
  }

  // 嘗試兌換 admin impersonate 設下的 HttpOnly cookie 為真正 auth_token。
  // cookie 由瀏覽器自動帶上；若沒有 cookie，server 會回 401，本地視為無事發生。
  // 注意：即使已登入也要嘗試，這樣 admin 切換登入別的玩家時才會覆蓋舊 token。
  async function captureTokenFromUrl() {
    try {
      // validateStatus 容許 401 不丟錯，避免觸發全域 401 interceptor
      const { data, status } = await api.post('/sessions/exchange', null, {
        validateStatus: (s) => s < 500
      })
      if (status === 200 && data?.success) {
        // 清掉前一個玩家的 cache（profile / 餘額），再寫新 token
        clear()
        setToken(data.data.auth_token)
        subscribeWallet(token.value)
        return true
      }
    } catch {
      // 沒 cookie 或網路失效都視為未登入
    }
    return false
  }

  async function login(account, password, turnstileToken) {
    const { data } = await api.post('/sessions', { account, password, turnstile_token: turnstileToken })
    if (data.success) {
      setToken(data.data.auth_token)
      profilePromise = null
      subscribeWallet(token.value)
      return { success: true }
    }
    return { success: false, error: data.error }
  }

  async function logout() {
    try {
      await api.delete('/sessions')
    } catch {
      // 即使 API 失敗也清 token
    }
    clear()
  }

  async function fetchWalletBalance() {
    if (!token.value) return
    try {
      const { data } = await api.get('/wallet')
      if (data.success) {
        walletBalance.value = data.data.balance
      }
    } catch {
      // 靜默失敗
    }
  }

  // 手動觸發（按鈕用）：cooldown 內回 false，spinner 期間 walletRefreshing=true
  async function refreshWalletBalance() {
    const now = Date.now()
    if (walletRefreshing.value) return false
    if (now - lastRefreshAt < REFRESH_COOLDOWN_MS) return false
    walletRefreshing.value = true
    lastRefreshAt = now
    try {
      await fetchWalletBalance()
      return true
    } finally {
      walletRefreshing.value = false
    }
  }

  function ensureProfile() {
    if (!token.value) return Promise.resolve(null)
    if (profilePromise) return profilePromise
    profilePromise = (async () => {
      try {
        const { data } = await api.get('/profile')
        if (data.success) {
          walletMode.value = data.data.wallet_mode
          playerAccount.value = data.data.account
          if (!localStorage.getItem('player_locale') && data.data.locale) {
            setLocale(data.data.locale)
          }
        }
      } catch {
        // 靜默；下次再試
        profilePromise = null
      }
    })()
    return profilePromise
  }

  // ── WebSocket 訂閱 ──────────────────────────────────────────────
  // WS 為唯一餘額通道；reconnect 時 ActionCable 自動重連
  // visibility / focus 回前景時補拉一次（cover WS reconnect 期間漏掉的事件）

  function subscribeWallet(authToken) {
    if (walletSubscription) return
    const consumer = connectCable(authToken)
    walletSubscription = consumer.subscriptions.create('PlayerChannel', {
      connected() {
        wsConnected.value = true
        // WS 接上：補拉一次當前餘額（避免錯過 connect 之前的事件）
        fetchWalletBalance()
      },
      disconnected() {
        wsConnected.value = false
      },
      received(data) {
        if (data?.type === 'main_wallet_updated') {
          walletBalance.value = parseFloat(data.balance)
        }
      }
    })
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
  }

  function unsubscribeWallet() {
    if (walletSubscription) {
      walletSubscription.unsubscribe()
      walletSubscription = null
    }
    disconnectCable()
    wsConnected.value = false
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('focus', onFocus)
  }

  function onVisibility() {
    if (document.visibilityState === 'visible' && token.value) fetchWalletBalance()
  }

  function onFocus() {
    if (token.value) fetchWalletBalance()
  }

  return {
    token,
    walletBalance,
    walletMode,
    locale,
    walletRefreshing,
    wsConnected,
    playerAccount,
    isAuthenticated,
    isSeamless,
    setToken,
    setLocale,
    clear,
    captureTokenFromUrl,
    login,
    logout,
    fetchWalletBalance,
    refreshWalletBalance,
    ensureProfile,
    subscribeWallet
  }
})
